import { Injectable, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { EnvironmentService } from '../settings/environment.service';

export type LoggerContext = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class LoggerService {
  // Console in non-production builds, Sentry in production — routed through EnvironmentService
  // so this no longer reads the raw environment.production flag directly.
  private readonly environmentService = inject(EnvironmentService);

  error(message: string, error?: unknown, context?: LoggerContext): void {
    const useSentry: boolean = this.environmentService.shouldEnableSentry();

    if (!useSentry) {
      console.error(message, error, context);
      return;
    }

    if (error) {
      const exception: unknown = error instanceof Error ? error : new Error(message);
      Sentry.captureException(exception, {
        extra: { ...context, errorContext: message, originalError: error }
      });
    } else {
      Sentry.captureMessage(message, { level: 'error', extra: context });
    }
  }

  warn(message: string, context?: LoggerContext): void {
    const useSentry: boolean = this.environmentService.shouldEnableSentry();

    if (!useSentry) {
      console.warn(message, context);
      return;
    }

    Sentry.captureMessage(message, { level: 'warning', extra: context });
  }

  info(message: string, context?: LoggerContext): void {
    const useSentry: boolean = this.environmentService.shouldEnableSentry();

    if (!useSentry) {
      console.info(message, context);
      return;
    }

    Sentry.addBreadcrumb({
      message,
      level: 'info',
      data: context
    });
  }

  debug(_message: string, _context?: LoggerContext): void {
    // No-op in production (tree-shaken)
    // In dev, intentionally silent to avoid console noise
  }
}
